import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast as message } from 'sonner'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Modal } from '../../components/ui/modal'
import { Field, Switch, Textarea } from '../../components/ui/controls'
import { DataTable, type Column } from '../../components/ui/data-table'
import {
  listPaymentMethods,
  updatePaymentMethodFeeSharing,
  updatePaymentMethodStatus,
} from '../../services/paymentMethods'
import { paymentMethodLabels } from '../../lib/payment-method-labels'
import { useMerchantStore } from '../../stores/merchantStore'
import type { MerchantPaymentMethod, MerchantUnboundPaymentMethod } from '../../types'

const methodName = (method: MerchantPaymentMethod | MerchantUnboundPaymentMethod) =>
  ('displayName' in method && method.displayName) ||
  paymentMethodLabels[method.paymentMethod] ||
  method.paymentMethod

const businessModeLabels: Record<
  MerchantPaymentMethod['businessMode'],
  string
> = {
  ONE_TIME: '一次性支付',
  SUBSCRIPTION: '订阅支付',
  OFF_SESSION: '自动充值',
}

type FeeSharingFormValues = {
  customerFeeRatePercent: number
  customerPerTransactionFee: number
  checkoutDisplayText?: string
}

type PaymentMethodRow =
  | (MerchantPaymentMethod & { kind: 'bound' })
  | (MerchantUnboundPaymentMethod & { kind: 'unbound' })

function formatRate(raw: string): string {
  return `${(Number(raw || 0) * 100).toFixed(2).replace(/\.?0+$/, '')}%`
}

function formatUsd(raw: string | number): string {
  return `USD ${Number(raw || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function rateToPercent(raw: string): number {
  return Number((Number(raw || 0) * 100).toFixed(6))
}

function percentToRateString(value?: number | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '0'
  return (value / 100).toFixed(8).replace(/0+$/, '').replace(/\.$/, '') || '0'
}

export default function PaymentMethods() {
  const verified = useMerchantStore((state) => (
    state.merchant?.kycStatus === 'APPROVED' || state.merchant?.kybStatus === 'APPROVED'
  ))
  const [methods, setMethods] = useState<PaymentMethodRow[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingMethod, setUpdatingMethod] = useState<string | null>(null)
  const [editingMethod, setEditingMethod] =
    useState<MerchantPaymentMethod | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const form = useForm<FeeSharingFormValues>()
  const customerFeeRatePercent = form.watch('customerFeeRatePercent')
  const customerPerTransactionFee = form.watch('customerPerTransactionFee')

  const totalFeeRatePercent = editingMethod
    ? rateToPercent(editingMethod.feeRate)
    : 0
  const totalPerTransactionFee = editingMethod
    ? Number(editingMethod.perTransactionFee || 0)
    : 0
  const merchantFeeRatePercent = Math.max(
    0,
    Number(
      (totalFeeRatePercent - Number(customerFeeRatePercent ?? 0)).toFixed(6),
    ),
  )
  const merchantPerTransactionFee = Math.max(
    0,
    Number(
      (totalPerTransactionFee - Number(customerPerTransactionFee ?? 0)).toFixed(
        6,
      ),
    ),
  )

  const fetchMethods = async () => {
    setLoading(true)
    try {
      const res = await listPaymentMethods()
      setMethods([
        ...res.data.paymentMethods.map((method) => ({ ...method, kind: 'bound' as const })),
        ...(res.data.unboundPaymentMethods ?? []).map((method) => ({ ...method, kind: 'unbound' as const })),
      ])
    } catch (error) {
      message.error(error instanceof Error ? error.message : '載入支付方式失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMethods()
  }, [])

  const replaceMethod = (updated: MerchantPaymentMethod) => {
    setMethods((prev) => prev.map((item) => (
      item.kind === 'bound' && item.bindingId === updated.bindingId ? { ...updated, kind: 'bound' } : item
    )))
  }

  const handleStatusChange = async (
    record: MerchantPaymentMethod,
    enabled: boolean,
  ) => {
    setUpdatingMethod(record.bindingId)
    try {
      const res = await updatePaymentMethodStatus(
        record.paymentMethod,
        enabled,
        record.bindingId,
      )
      replaceMethod(res.data)
      message.success(enabled ? '支付方式已啟用' : '支付方式已禁用')
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '更新支付方式狀態失敗',
      )
    } finally {
      setUpdatingMethod(null)
    }
  }

  const openFeeSharingModal = (record: MerchantPaymentMethod) => {
    setEditingMethod(record)
    form.reset({
      customerFeeRatePercent: rateToPercent(record.customerFeeRate),
      customerPerTransactionFee: Number(record.customerPerTransactionFee || 0),
      checkoutDisplayText: record.checkoutDisplayText || undefined,
    })
    setModalOpen(true)
  }

  const handleSubmitFeeSharing = async (values: FeeSharingFormValues) => {
    if (!editingMethod) return
    setUpdatingMethod(editingMethod.bindingId)
    try {
      const res = await updatePaymentMethodFeeSharing(
        editingMethod.paymentMethod,
        {
          customerFeeRate: percentToRateString(values.customerFeeRatePercent),
          customerPerTransactionFee: String(
            values.customerPerTransactionFee ?? 0,
          ),
          checkoutDisplayText: values.checkoutDisplayText?.trim() || undefined,
          bindingId: editingMethod.bindingId,
        },
      )
      replaceMethod(res.data)
      setModalOpen(false)
      setEditingMethod(null)
      message.success('手续费承担方式已更新')
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '更新手续费承担方式失败',
      )
    } finally {
      setUpdatingMethod(null)
    }
  }

  const columns: Column<PaymentMethodRow>[] = [
    { key: 'method', header: '支付方式', render: methodName },
    {
      key: 'businessMode',
      header: '业务场景',
      render: (record) => businessModeLabels[record.businessMode] || record.businessMode,
    },
    {
      key: 'status',
      header: '状态',
      render: (record) => record.kind === 'bound' ? (
        <span className={record.enabled ? 'rounded bg-emerald-50 px-2 py-1 text-emerald-700' : 'text-muted-foreground'}>
          {record.enabled ? '已启用' : '已禁用'}
        </span>
      ) : (
        <div className="flex flex-col gap-1">
          <span className="text-amber-700">未开通</span>
          {!record.channelEnabled && <span className="text-xs text-muted-foreground">平台暂不可用</span>}
        </div>
      ),
    },
    ...(['fee', 'customer', 'merchant'] as const).map((kind): Column<PaymentMethodRow> => ({
      key: kind,
      className: 'text-right tabular-nums',
      header: kind === 'fee' ? '订单手续费' : kind === 'customer' ? '客户承担' : '商户承担',
      render: (record) => record.kind === 'unbound' ? '—' : (
        <div>
          <p>{formatRate(kind === 'fee' ? record.feeRate : kind === 'customer' ? record.customerFeeRate : record.merchantFeeRate)}</p>
          <p className="text-muted-foreground">
            {formatUsd(kind === 'fee' ? record.perTransactionFee : kind === 'customer' ? record.customerPerTransactionFee : record.merchantPerTransactionFee)} / 笔
          </p>
        </div>
      ),
    })),
    {
      key: 'actions',
      header: '操作',
      className: 'whitespace-nowrap',
      render: (record) => record.kind === 'unbound' ? (
        <span className="text-muted-foreground">{verified ? '请联系客服开通' : '先通过 KYB / KYC'}</span>
      ) : (
        <div className="flex items-center gap-3">
          <Switch
            checked={record.enabled}
            disabled={updatingMethod === record.bindingId}
            onCheckedChange={(checked) => void handleStatusChange(record, checked)}
          />
          <Button
            variant="outline"
            disabled={updatingMethod === record.bindingId}
            onClick={() => openFeeSharingModal(record)}
          >
            设置手续费承担方式
          </Button>
        </div>
      ),
    },
  ]
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">支付方式</h1>
      <p className="rounded-xl border border-primary/15 bg-primary/5 p-4 text-sm">
        展示平台配置的所有支付方式。非默认开通的支付方式需通过 KYB / KYC 审核后，联系客服开通（support@kyrenpay.com）。
        已开通方式的手续费承担设置会影响客户在 Checkout 页面看到的应付金额。
      </p>
      <Card>
        <DataTable
          columns={columns}
          data={methods}
          rowKey={(record) => record.kind === 'bound' ? `binding:${record.bindingId}` : `unbound:${record.paymentMethod}:${record.businessMode}`}
          loading={loading}
          emptyText="暂无支付方式"
        />
      </Card>
      <Modal
        className="max-w-xl"
        description="設定客戶承擔部分，商戶承擔金額將自動計算。"
        title={
          editingMethod
            ? `设置 ${methodName(editingMethod)} 手续费承担方式`
            : '设置手续费承担方式'
        }
        open={modalOpen}
        closable={!updatingMethod}
        onOpenChange={(open) => {
          if (!updatingMethod) {
            setModalOpen(open)
            if (!open) setEditingMethod(null)
          }
        }}
      >
        <form
          className="space-y-5"
          onSubmit={form.handleSubmit(handleSubmitFeeSharing)}
        >
          <section aria-label="订单费用合计" className="rounded-lg border border-border bg-muted/40 p-4">
            <h3 className="mb-3 text-sm font-semibold">订单费用合计</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">订单手续费率</dt>
                <dd className="shrink-0 font-semibold tabular-nums">{totalFeeRatePercent.toFixed(4)}%</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">订单每笔固定手续费</dt>
                <dd className="shrink-0 font-semibold tabular-nums">{formatUsd(totalPerTransactionFee)}</dd>
              </div>
            </dl>
          </section>
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold">费率分摊</legend>
            <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="客户承担费率 (%)"
            htmlFor="customer-rate"
            required
            error={form.formState.errors.customerFeeRatePercent?.message}
          >
            <Input
              id="customer-rate"
              type="number"
              min={0}
              max={totalFeeRatePercent}
              step="0.0001"
              {...form.register('customerFeeRatePercent', {
                required: '请输入客户承担费率',
                valueAsNumber: true,
                min: 0,
                max: {
                  value: totalFeeRatePercent,
                  message: '客户承担费率不能高于订单手续费率',
                },
              })}
            />
          </Field>
          <Field label="商户承担费率 (%)">
            <Input value={merchantFeeRatePercent.toFixed(4)} className="tabular-nums" disabled />
          </Field>
            </div>
          </fieldset>
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold">每笔固定手续费分摊</legend>
            <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="客户承担每笔固定手续费 (USD)"
            htmlFor="customer-fixed"
            required
            error={form.formState.errors.customerPerTransactionFee?.message}
          >
            <Input
              id="customer-fixed"
              type="number"
              min={0}
              max={totalPerTransactionFee}
              step="0.0001"
              {...form.register('customerPerTransactionFee', {
                required: '请输入客户承担每笔固定手续费',
                valueAsNumber: true,
                min: 0,
                max: {
                  value: totalPerTransactionFee,
                  message: '客户承担每笔固定手续费不能高于订单每笔固定手续费',
                },
              })}
            />
          </Field>
          <Field label="商户承担每笔固定手续费 (USD)">
            <Input value={merchantPerTransactionFee.toFixed(2)} className="tabular-nums" disabled />
          </Field>
            </div>
          </fieldset>
          <Field label="Checkout 展示文案" htmlFor="checkout-text">
            <Textarea
              id="checkout-text"
              rows={2}
              maxLength={80}
              placeholder="例如：支付处理费"
              {...form.register('checkoutDisplayText')}
            />
          </Field>
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button type="submit" disabled={Boolean(updatingMethod)}>
              保存
            </Button>
            <Button
              variant="outline"
              disabled={Boolean(updatingMethod)}
              onClick={() => {
                setModalOpen(false)
                setEditingMethod(null)
              }}
            >
              取消
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
